import { Observable } from 'rxjs';
import {
  AsistenciaEntity, BlobEntity, CicloEntity, DatosCiclo, GrupoEntity, HabilitacionEntity, InscripcionEntity, KvEntity,
  ModuloEntity, ParticipanteEntity, RolFormativoEntity, SeguimientoEntity, SesionEntity, SolicitudEntity, UsuarioEntity
} from '../models/asistencia.model';
import { AlertaEntity, CorreoSalidaEntity, PlantillaCorreoEntity, PreferenciaNotificacionEntity } from '../models/alertas.model';
import { EventoAuditoria } from '../models/auditoria.model';

export interface EntidadesPorTabla {
  ciclos: CicloEntity;
  roles: RolFormativoEntity;
  modulos: ModuloEntity;
  grupos: GrupoEntity;
  sesiones: SesionEntity;
  usuarios: UsuarioEntity;
  participantes: ParticipanteEntity;
  habilitaciones: HabilitacionEntity;
  inscripciones: InscripcionEntity;
  asistencias: AsistenciaEntity;
  solicitudes: SolicitudEntity;
  seguimientos: SeguimientoEntity;
  blobs: BlobEntity;
  alertas: AlertaEntity;
  correosSalida: CorreoSalidaEntity;
  preferencias: PreferenciaNotificacionEntity;
  plantillas: PlantillaCorreoEntity;
  kv: KvEntity;
}

export type TablaAsistencia = keyof EntidadesPorTabla;
export type TablaConId = Exclude<TablaAsistencia, 'preferencias' | 'plantillas' | 'kv'>;

/**
 * Unidad de trabajo: todo lo que se haga dentro de `transaccion()` es atómico
 * (cambio + auditoría + alertas + correos). En MJ equivale a una transacción `pg`.
 */
export interface UnidadTrabajo {
  datosCiclo(cicloId: string): Promise<DatosCiclo>;
  obtener<K extends TablaConId>(tabla: K, id: string): Promise<EntidadesPorTabla[K] | undefined>;
  guardar<K extends TablaAsistencia>(tabla: K, filas: EntidadesPorTabla[K][]): Promise<void>;
  todos<K extends TablaAsistencia>(tabla: K): Promise<EntidadesPorTabla[K][]>;
  ultimoEvento(): Promise<EventoAuditoria | undefined>;
  agregarEvento(evento: EventoAuditoria): Promise<number>;
  eventosDesde(iso: string): Promise<EventoAuditoria[]>;
  dedupExistentes(claves: string[]): Promise<Set<string>>;
  alertasAbiertas(): Promise<AlertaEntity[]>;
  correosCreadosDesde(iso: string): Promise<CorreoSalidaEntity[]>;
  kvObtener<T>(clave: string): Promise<T | undefined>;
  kvGuardar(clave: string, valor: unknown): Promise<void>;
}

/** Puerto de persistencia (ADR-01). Adaptador MVP: IndexedDB; integración: HTTP. */
export abstract class AsistenciaRepository {
  abstract transaccion<T>(fn: (uow: UnidadTrabajo) => Promise<T>): Promise<T>;
  abstract observarCiclos(): Observable<CicloEntity[]>;
  abstract observarDatosCiclo(cicloId: string): Observable<DatosCiclo>;
  abstract observarAlertasDe(destinatarioId: string): Observable<AlertaEntity[]>;
  abstract observarTodasAlertas(): Observable<AlertaEntity[]>;
  abstract observarCorreos(): Observable<CorreoSalidaEntity[]>;
  abstract observarAuditoria(): Observable<EventoAuditoria[]>;
  abstract observarPreferencias(usuarioId: string): Observable<PreferenciaNotificacionEntity[]>;
  abstract observarPlantillas(): Observable<PlantillaCorreoEntity[]>;
  abstract observarKv<T>(clave: string): Observable<T | undefined>;
  abstract obtenerBlob(id: string): Promise<BlobEntity | undefined>;
  abstract todosEventos(): Promise<EventoAuditoria[]>;
}
